//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React, { useCallback, useMemo, useState } from 'react';
import { QueryContext, type QueryContextValue } from '@/contexts/QueryContext';
import { queryService } from '@/services';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';

interface QueryProviderProps {
  children: React.ReactNode;
}

// Owns the query-editor state (question, query) and the pure query-execution
// methods.  Keeping question/query here means typing in the query editor no
// longer recomputes the main context value.
const QueryProvider = ({ children }: QueryProviderProps) => {
  const handleError = useApiErrorHandler();

  const [question, setQuestion] = useState('');
  const [query, setQuery] = useState('');

  const submitQuery = useCallback((query: string, dataset_name: string) =>
    handleError('submitQuery',
                () => queryService.submitQuery(query, dataset_name)),
    [handleError]);

  const waitQuery = useCallback((
    jobId: string, offset: number, length: number
  ) =>
    handleError('waitQuery',
                () => queryService.waitQuery(jobId, offset, length)),
    [handleError]);

  const getAnswer = useCallback((
    jobId: string, offset: number, length: number
  ) =>
    handleError('getAnswer',
                () => queryService.getAnswer(jobId, offset, length)),
    [handleError]);

  const value = useMemo((): QueryContextValue => ({
    question, setQuestion,
    query, setQuery,
    submitQuery, waitQuery, getAnswer,
  }), [question, query, submitQuery, waitQuery, getAnswer]);

  return (
    <QueryContext.Provider value={value}>
      {children}
    </QueryContext.Provider>
  );
};

export default QueryProvider;
